import { getDeviceBindingPayload } from '../lib/deviceBinding';
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BackgroundBlobs from '../components/BackgroundBlobs';
import LegalLinks from '../components/LegalLinks';
import OpenInBrowserModal from '../components/OpenInBrowserModal';
import { LockKeyhole, Mail, ShieldCheck, UserRound } from 'lucide-react';
import { getPostLoginPath, isAdminSubdomainHost, isAdminUIHostAllowed } from '../lib/siteRouting';
import { useAdminSession } from '../hooks/useAdminSession';
import { detectInAppBrowser, shouldShowOpenInBrowserModal } from '../lib/inAppBrowser';

const isHardayAndroidApk = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search || '');
  if (params.get('source') === 'android_apk') {
    localStorage.setItem('hardayClientSource', 'android_apk');
    return true;
  }
  if (localStorage.getItem('hardayClientSource') === 'android_apk') return true;
  return /HardayWebViewApp/i.test(window.navigator?.userAgent || '');
};

const Login = () => {
  const { isAuthenticated, loading, setReferrer, loginWithGoogleIdToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminSubmitting, setAdminSubmitting] = useState(false);
  // In-app browser (Instagram/Facebook/Messenger/Telegram/…) detection. When
  // Harday is opened inside one of these embedded browsers we nudge the user to
  // reopen in their phone's default browser, where Google Login works reliably.
  const inAppInfo = useMemo(() => detectInAppBrowser(), []);
  const [showBrowserModal, setShowBrowserModal] = useState(false);
  const nextPath = searchParams.get('next') || '';
  const googleError = searchParams.get('google_error') || '';
  // Show the admin login form on the production admin subdomain AND on
  // preview/dev hosts when the user explicitly navigates to /admin or
  // /admin-login (so admin flows can be tested without a real subdomain).
  const isAdminLoginHost = isAdminSubdomainHost() || (
    isAdminUIHostAllowed() && (
      (typeof window !== 'undefined' && (
        window.location.pathname === '/admin' ||
        window.location.pathname === '/admin-login' ||
        window.location.pathname === '/harday-admin' ||
        nextPath === '/admin' ||
        nextPath === '/harday-admin'
      ))
    )
  );
  const {
    adminAccount,
    adminLoading,
    adminLogin,
  } = useAdminSession(isAdminLoginHost);

  const resolvePostLoginPath = useMemo(
    () => () => getPostLoginPath({ nextPath, isAdminUser: false }),
    [nextPath]
  );

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      localStorage.setItem('hardayReferrer', ref);
      if (setReferrer) setReferrer(ref);
    }
  }, [searchParams, setReferrer]);

  useEffect(() => {
    if (isAdminLoginHost) {
      if (!adminLoading && adminAccount) {
        navigate('/admin', { replace: true });
      }
      return;
    }

    if (!loading && isAuthenticated) {
      navigate(resolvePostLoginPath(), { replace: true });
    }
  }, [adminAccount, adminLoading, isAdminLoginHost, isAuthenticated, loading, navigate, resolvePostLoginPath]);

  // Show the "Open in Browser" nudge only on the regular (non-admin) login
  // screen when we're inside a social in-app browser and it hasn't been
  // dismissed for this session.
  useEffect(() => {
    if (isAdminLoginHost) return;
    if (shouldShowOpenInBrowserModal()) {
      setShowBrowserModal(true);
    }
  }, [isAdminLoginHost]);

  // Is the Harday APK's native Google Sign-In bridge present AND configured?
  // Older APKs won't have the methods (so this is false and we use browser
  // OAuth), and a build without a real Web client ID reports itself unavailable.
  const isNativeGoogleSignInAvailable = () => {
    if (typeof window === 'undefined') return false;
    const bridge = window.HardayAndroid;
    if (!bridge || typeof bridge.startNativeGoogleSignIn !== 'function') return false;
    try {
      if (typeof bridge.isNativeGoogleSignInAvailable === 'function') {
        return Boolean(bridge.isNativeGoogleSignInAvailable());
      }
    } catch {
      return false;
    }
    return false;
  };

  // Existing browser-based Google OAuth flow. Unchanged behaviour: the website
  // always uses this, and the APK falls back to it when native is unavailable.
  const startBrowserOAuth = async () => {
    const currentRef = searchParams.get('ref') || localStorage.getItem('hardayReferrer');
    const redirectPath = resolvePostLoginPath();
    const params = new URLSearchParams();
    params.set('next', redirectPath || '/dashboard');
    if (currentRef) {
      params.set('ref', currentRef);
    }
    const isApkLogin = isHardayAndroidApk();
    if (isApkLogin) {
      params.set('apk', '1');
    }
    const deviceBinding = await getDeviceBindingPayload();
    if (deviceBinding.deviceFingerprint) params.set('device', deviceBinding.deviceFingerprint);
    if (deviceBinding.platform) params.set('platform', deviceBinding.platform);
    const googleLoginUrl = `${window.location.origin}/api/auth/google/login?${params.toString()}`;
    // Runtime breadcrumb for Android WebView debugging. The APK also logs this
    // navigation in shouldOverrideUrlLoading before it leaves the WebView.
    console.info('[HardayLogin] Continue with Google (browser OAuth)', { googleLoginUrl, isApkLogin });
    window.location.href = googleLoginUrl;
  };

  // Drive the APK native flow: ask the bridge to open the account picker and
  // wait for the ID token (or an error) it dispatches back as a window event.
  // Resolves with one of: 'success' (logged in), 'cancelled' (user dismissed —
  // stay put), or 'fallback' (use browser OAuth). The native side always emits
  // exactly one event, so the long timeout is only a defensive safety net.
  const attemptNativeGoogleSignIn = () =>
    new Promise((resolve) => {
      let settled = false;
      let timer = null;
      const cleanup = () => {
        if (timer) clearTimeout(timer);
        window.removeEventListener('harday-google-idtoken', onToken);
        window.removeEventListener('harday-google-signin-error', onError);
      };
      const onToken = async (event) => {
        if (settled) return;
        settled = true;
        cleanup();
        const idToken = event?.detail;
        if (!idToken) {
          resolve({ outcome: 'fallback' });
          return;
        }
        const result = await loginWithGoogleIdToken(idToken);
        resolve(result?.success ? { outcome: 'success' } : { outcome: 'fallback' });
      };
      const onError = (event) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve({ outcome: event?.detail === 'cancelled' ? 'cancelled' : 'fallback' });
      };
      window.addEventListener('harday-google-idtoken', onToken);
      window.addEventListener('harday-google-signin-error', onError);
      try {
        window.HardayAndroid.startNativeGoogleSignIn();
      } catch (error) {
        if (!settled) {
          settled = true;
          cleanup();
          resolve({ outcome: 'fallback' });
        }
        return;
      }
      timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          cleanup();
          resolve({ outcome: 'fallback' });
        }
      }, 180000);
    });

  const handleGoogleLogin = async () => {
    if (isAdminLoginHost) {
      return;
    }
    if (googleSubmitting) {
      return;
    }
    setGoogleSubmitting(true);
    try {
      if (isHardayAndroidApk() && isNativeGoogleSignInAvailable()) {
        console.info('[HardayLogin] Attempting native Google Sign-In');
        const result = await attemptNativeGoogleSignIn();
        if (result.outcome === 'success') {
          navigate(resolvePostLoginPath(), { replace: true });
          return;
        }
        if (result.outcome === 'cancelled') {
          // User dismissed the account picker — leave them on the login screen.
          return;
        }
        // 'fallback' — native unavailable/failed; use the browser OAuth flow.
        console.info('[HardayLogin] Native sign-in unavailable; falling back to browser OAuth');
      }
      await startBrowserOAuth();
    } finally {
      setGoogleSubmitting(false);
    }
  };

  const handleAdminLogin = async () => {
    setAdminError('');
    setAdminSubmitting(true);

    const result = await adminLogin({
      username: adminUsername.trim(),
      password: adminPassword,
    });

    if (result.success) {
      navigate('/admin', { replace: true });
    } else {
      setAdminError(result.error || 'Invalid username or password');
    }

    setAdminSubmitting(false);
  };

  const title = isAdminLoginHost ? 'Admin Access' : 'Get Started';
  const subtitle = isAdminLoginHost
    ? 'Sign in with your secure admin username and password'
    : 'Maybe the next legend is entering';

  return (
    <div className="w-full max-w-md mx-auto min-h-screen relative overflow-hidden bg-white">
      <OpenInBrowserModal
        isOpen={showBrowserModal}
        appName={inAppInfo.appName}
        platform={inAppInfo.platform}
        onClose={() => setShowBrowserModal(false)}
      />
      <BackgroundBlobs />

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-6">
        <motion.div
          className="w-full"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 24 }}
        >
          <div className="text-center mb-10">
            <h1 className="text-6xl font-bold text-gradient font-['Outfit'] mb-3">
              Harday
            </h1>
            <p className="text-base text-[#666666] font-['DM_Sans'] font-medium">
              {isAdminLoginHost ? 'Secure admin access only.' : 'One legend every day.'}
            </p>
          </div>

          <div className="glass-panel rounded-[24px] p-6 space-y-4">
            <h2 className="text-2xl font-bold text-[#000000] mb-2 font-['Outfit'] text-center">
              {title}
            </h2>
            <p className="text-sm text-[#666666] font-['DM_Sans'] text-center mb-4">
              {subtitle}
            </p>

            {isAdminLoginHost ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#666666] mb-1 font-['DM_Sans']">Username</label>
                  <div className="relative">
                    <UserRound className="w-4 h-4 text-[#9333EA] absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={adminUsername}
                      onChange={(e) => setAdminUsername(e.target.value)}
                      autoComplete="username"
                      className="w-full rounded-[16px] border border-gray-200 bg-white py-3 pl-10 pr-4 text-[#000000] outline-none focus:border-[#9333EA]/40"
                      placeholder="Enter admin username"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-[#666666] mb-1 font-['DM_Sans']">Password</label>
                  <div className="relative">
                    <LockKeyhole className="w-4 h-4 text-[#9333EA] absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      autoComplete="current-password"
                      className="w-full rounded-[16px] border border-gray-200 bg-white py-3 pl-10 pr-4 text-[#000000] outline-none focus:border-[#9333EA]/40"
                      placeholder="Enter admin password"
                    />
                  </div>
                </div>

                <motion.button
                  onClick={handleAdminLogin}
                  data-testid="admin-login-btn"
                  disabled={adminSubmitting || adminLoading}
                  className="w-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] to-[#7C3AED] text-white py-3.5 rounded-full font-semibold flex items-center justify-center gap-3 disabled:opacity-60"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ShieldCheck className="w-5 h-5" />
                  <span className="font-['DM_Sans']">{adminSubmitting ? 'Signing In...' : 'Sign In as Admin'}</span>
                </motion.button>

                {adminError && (
                  <p className="text-xs text-[#B91C1C] bg-[#FEE2E2] rounded-[12px] px-3 py-2 font-['DM_Sans']">
                    {adminError}
                  </p>
                )}
              </div>
            ) : (
              <>
                <motion.button
                  onClick={handleGoogleLogin}
                  disabled={googleSubmitting}
                  data-testid="google-login-btn"
                  className="w-full bg-white border-2 border-gray-200 text-[#000000] py-3.5 rounded-full font-semibold flex items-center justify-center gap-3 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-60"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Mail className="w-5 h-5 text-[#EA4335]" />
                  <span className="font-['DM_Sans']">{googleSubmitting ? 'Signing in…' : 'Continue with Google'}</span>
                </motion.button>

                {googleError && (
                  <p className="text-xs text-[#B91C1C] bg-[#FEE2E2] rounded-[12px] px-3 py-2 font-['DM_Sans']">
                    Google login failed. Please try again.
                  </p>
                )}
              </>
            )}
          </div>

          <p className="text-xs text-center text-[#666666] mt-6 font-['DM_Sans']">
            By continuing, you agree to our
          </p>
          <LegalLinks className="mt-1" align="center" tone="muted" />
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
